import axios from 'axios';

const connectItem = ({ publicToken, companyId }) =>
  axios.post('/integrations/connect_item', {
    public_token: publicToken,
    company_id: companyId,
  });
const enableItem = id => axios.post('/integrations/enable_item', { id });
const connectQuickBooks = ({ code, companyId, realmId }) =>
  axios.post('/integrations/connect_qb', {
    code,
    company_id: companyId,
    realm_id: realmId,
  });
const disconnectQuickBooks = companyId =>
  axios.post('/integrations/disconnect_qb', { company_id: companyId });
const createPublicToken = id =>
  axios.post('/integrations/create_public_token', { id });
const getAllBanks = companyId =>
  axios.get(`/integrations/get_all_bank_integrations/?company_id=${companyId}`);
const deleteBank = ({ id }) => axios.delete(`/integrations/${id}/delete/`);
const createAccounts = ({ id, accounts }) =>
  axios.post('/integrations/create_accounts', {
    accounts,
    id,
  });
const saveMyCloud = ({ ramSize, storageSize, cloudAccessUsers, backupPlan }) =>
  axios.post('integrations/save_my_cloud', {
    ram_size: ramSize,
    storage_size: storageSize,
    cloud_access_users: cloudAccessUsers,
    backup_plan: backupPlan,
  });
const updateAccount = ({ account }) =>
  axios.patch(`/integrations/${account.id}/update_account`, {
    account,
  });
const getAllAccounts = companyId =>
  axios.post('integrations/get_all_accounts', {
    company_id: companyId,
  });
const getDocuments = ({ slug, token, companyId, path }) =>
  axios.post(
    '/integrations/get_documents',
    slug && token
      ? {
          slug,
          token,
          path,
        }
      : {
          company_id: companyId,
          path,
        },
  );
const searchDocuments = ({ slug, token, companyId, searchTerm }) =>
  axios.post(
    '/integrations/search_documents',
    slug && token
      ? {
          slug,
          token,
          search_term: searchTerm,
        }
      : {
          company_id: companyId,
          search_term: searchTerm,
        },
  );
const getPathType = ({ slug, token, companyId, path }) =>
  axios.post(
    '/integrations/get_path_type',
    slug && token
      ? {
          slug,
          token,
          path,
        }
      : {
          company_id: companyId,
          path,
        },
  );
const getTemporaryUploadLink = ({ slug, token, companyId } = {}) => {
  return axios.get(
    slug && token
      ? `/integrations/get_temporary_upload_link?slug=${slug}&token=${token}`
      : `/integrations/get_temporary_upload_link?company_id=${companyId}`,
  );
};

const uploadToDropbox = (uploadLink, data) => {
  const url = new URL(uploadLink);

  if (url.hostname.includes('dropbox')) {
    return axios.post(uploadLink, data, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
  } else {
    return axios.put(uploadLink, data, {
      headers: {
        'Content-Type': data.type,
      },
    });
  }
};

const processDocument = ({
  token,
  slug,
  id,
  documentName,
  path,
  companyId,
  questionnaireId,
  conversationId,
  taxYear,
  documentType,
  markedAsOthers,
  selectedOptions,
  description,
  tags,
  sectionId,
  requiresSplitting,
  taxSummaryEnabled,
}) => {
  return axios.post(
    '/integrations/process_document',
    slug && token
      ? {
          slug,
          token,
          id,
          path,
          document_name: documentName,
          questionnaire_id: questionnaireId,
          conversation_id: conversationId,
          marked_as_others: markedAsOthers,
          selected_options: selectedOptions,
          description: description,
          tags,
          section_id: sectionId,
          requires_splitting: requiresSplitting,
          tax_summary_enabled: taxSummaryEnabled,
        }
      : {
          id,
          document_name: documentName,
          path,
          company_id: companyId,
          questionnaire_id: questionnaireId,
          conversation_id: conversationId,
          document_type: documentType,
          tax_year: taxYear,
          marked_as_others: markedAsOthers,
          selected_options: selectedOptions,
          description: description,
          tags,
          section_id: sectionId,
          requires_splitting: requiresSplitting,
          tax_summary_enabled: taxSummaryEnabled,
        },
  );
};
const processBulkDocument = ({
  token,
  slug,
  id,
  documentName,
  path,
  companyId,
  questionnaireId,
}) => {
  return axios.post(
    '/integrations/process_bulk_document',
    slug && token
      ? {
          slug,
          token,
          id,
          path,
          document_name: documentName,
          questionnaire_id: questionnaireId,
        }
      : {
          id,
          document_name: documentName,
          path,
          company_id: companyId,
          questionnaire_id: questionnaireId,
        },
  );
};
const identifyAndMatchDocument = ({ extractedDocumentId, companyId }) => {
  return axios.post('integrations/identify_and_match_document', {
    company_id: companyId,
    extracted_document_id: extractedDocumentId,
  });
};

const autoSplitDocuments = ({ extractedDocumentId, companyId }) => {
  return axios.post('integrations/auto_split_documents', {
    company_id: companyId,
    extracted_document_id: extractedDocumentId,
  });
};

const renameItem = ({ slug, token, itemName, newName, path, companyId }) =>
  axios.post(
    '/integrations/rename_item',
    slug && token
      ? {
          slug,
          token,
          item_name: itemName,
          new_name: newName,
          path,
        }
      : {
          item_name: itemName,
          new_name: newName,
          path,
          company_id: companyId,
        },
  );
const moveItem = ({
  slug,
  token,
  oldPath,
  newCompanyId,
  newPath,
  deleteOldPath,
  companyId,
  isFolder,
}) =>
  axios.post(
    '/integrations/move_item',
    slug && token
      ? {
          slug,
          token,
          old_path: oldPath,
          new_path: newPath,
          delete_old_path: deleteOldPath,
          is_folder: isFolder,
        }
      : {
          old_path: oldPath,
          new_path: newPath,
          new_company_id: newCompanyId,
          delete_old_path: deleteOldPath,
          company_id: companyId,
          is_folder: isFolder,
        },
  );
const downloadDocument = ({
  slug,
  token,
  companyId,
  link,
  includeAnnotations,
  extractedDocumentId,
}) =>
  axios.post(
    '/integrations/download_document',
    slug && token
      ? {
          slug,
          token,
          link,
        }
      : {
          company_id: companyId,
          link,
          include_annotations: includeAnnotations,
          extracted_document_id: extractedDocumentId,
        },
  );
const downloadFolder = ({ slug, token, companyId, link, name }) =>
  axios
    .post(
      '/integrations/download_folder',
      slug && token
        ? {
            slug,
            token,
            link,
          }
        : {
            company_id: companyId,
            link,
          },
      {
        responseType: 'blob',
      },
    )
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name + '.zip');
      document.body.appendChild(link);
      link.click();
    });
const downloadMultipleDocuments = ({
  companyId,
  documents,
  convertImageToPdf,
}) =>
  axios.post('/integrations/download_multiple_documents', {
    company_id: companyId,
    documents,
    convert_image_to_pdf: convertImageToPdf,
  });
const convertToPdfsAndDownloadFolder = ({ companyId, link }) =>
  axios.post('/integrations/convert_to_pdfs_and_download_folder', {
    link,
    company_id: companyId,
  });

const getDocumentsUploadedOutsideOfQuestionnaire = companyId =>
  axios.get(
    `/integrations/get_documents_uploaded_outside_of_questionnaire?company_id=${companyId}`,
  );

const getTemporaryUploadLinkForUserImportDocument = () =>
  axios.get('/integrations/get_temporary_upload_link_for_user_import_document');

const uploadUserImportDocument = ({
  id,
  documentName,
  documentType,
  taxSoftware,
  year,
  comment,
  assignedFirmUserId,
}) =>
  axios.post('/integrations/upload_user_import_document', {
    id,
    year,
    comment,
    document_name: documentName,
    document_type: documentType,
    tax_software: taxSoftware,
    assigned_firm_user_id: assignedFirmUserId,
  });

const createFolder = ({ slug, token, path, companyId }) =>
  axios.post(
    '/integrations/create_folder',
    slug && token
      ? {
          slug,
          token,
          path,
        }
      : {
          path,
          company_id: companyId,
          token,
        },
  );
const deleteDocument = ({
  slug,
  token,
  documentName,
  path,
  companyId,
  isFolder,
}) =>
  axios.post(
    '/integrations/delete_document',
    slug && token
      ? {
          slug,
          token,
          document_name: documentName,
          path,
          is_folder: isFolder,
        }
      : {
          document_name: documentName,
          path,
          company_id: companyId,
          is_folder: isFolder,
        },
  );
const deletePath = ({ slug, token, path, companyId }) =>
  axios.post(
    '/integrations/delete_path',
    slug && token
      ? {
          slug,
          token,
          path,
        }
      : {
          path,
          company_id: companyId,
        },
  );
const connectStripe = ({ companyId, code }) =>
  axios.post('/integrations/connect_stripe', {
    code,
    company_id: companyId,
  });

const getStripeOAuthURL = () => axios.get('/integrations/stripe_oauth_url');
const stripeOAuthCallback = ({ code }) =>
  axios.post('/integrations/stripe_oauth_callback', { code });
const getOrCreateZapierAPIKey = () =>
  axios.post('/integrations/get_or_create_zapier_integration');

const stopTaxSoftwareParser = ({ firmId }) =>
  axios.post('/integrations/stop_tax_software_parser', {
    firm_id: firmId,
  });
const getTaxSoftwareIntegrations = () =>
  axios.get('/integrations/tax_software_integrations');
const createTaxSoftwareIntegration = ({ username, password, taxSoftware }) =>
  axios.post('/integrations/create_tax_software_integration', {
    username,
    password,
    tax_software: taxSoftware,
  });
const saveApprovedIPAddresses = ({ ipAddressToAdd, ipAddressToRemove }) =>
  axios.post('/integrations/save_approved_ip_addresses', {
    ip_addresses_to_add: ipAddressToAdd,
    ip_addresses_to_remove: ipAddressToRemove,
  });
const updateTaxSoftwarePasswords = ({ rdpPassword, taxSoftwarePassword }) =>
  axios.post('/integrations/update_tax_software_passwords', {
    rdp_password: rdpPassword,
    tax_software_password: taxSoftwarePassword,
  });
const getCurrentCompany = ({ slug, token }) =>
  axios.post(`/integrations/get_current_company`, {
    slug,
    token,
  });
const getStripeAccountLink = () =>
  axios.get(`/integrations/get_stripe_account_link`);
const checkSharepointIntegration = () =>
  axios.get(`/integrations/check_sharepoint_integration`);
const checkStripeIntegration = () =>
  axios.get(`/integrations/check_stripe_integration`);
const getAllWebhooks = () => axios.get(`/integrations/get_all_webhooks`);
const addWebhook = ({ hookUrl, hookType }) =>
  axios.post(`/integrations/add_webhook`, {
    hook_url: hookUrl,
    hook_type: hookType,
  });
const deleteWebhook = ({ id }) =>
  axios.post(`/integrations/delete_webhook`, { id });

const deleteTargetSheet = ({ documentType }) =>
  axios.delete(
    '/v2/integrations/target_sheets/delete_by_document_type?document_type=' +
      documentType,
  );

const getUploadedTargetSheets = () =>
  axios.get('/v2/integrations/target_sheets');

const getTemporaryUploadLinkForTargetSheets = () =>
  axios.get('/v2/integrations/target_sheets/get_temporary_upload_link');

const processTargetSheet = ({ id, documentType, documentName }) =>
  axios.post('/v2/integrations/target_sheets/process_uploaded', {
    id,
    document_type: documentType,
    document_name: documentName,
  });

const resyncSharepoint = () =>
  axios.post('/integrations/resync_sharepoint', {});

export default {
  connectItem,
  enableItem,
  createPublicToken,
  getAllBanks,
  createAccounts,
  updateAccount,
  deleteBank,
  getDocuments,
  searchDocuments,
  getPathType,
  connectStripe,
  getAllAccounts,
  connectQuickBooks,
  disconnectQuickBooks,
  getTemporaryUploadLink,
  uploadToDropbox,
  processDocument,
  processBulkDocument,
  identifyAndMatchDocument,
  autoSplitDocuments,
  downloadDocument,
  downloadFolder,
  downloadMultipleDocuments,
  convertToPdfsAndDownloadFolder,
  deleteDocument,
  createFolder,
  renameItem,
  moveItem,
  getTemporaryUploadLinkForUserImportDocument,
  uploadUserImportDocument,
  getTemporaryUploadLinkForTargetSheets,
  processTargetSheet,
  deleteTargetSheet,
  getUploadedTargetSheets,
  getDocumentsUploadedOutsideOfQuestionnaire,
  deletePath,
  getOrCreateZapierAPIKey,
  getTaxSoftwareIntegrations,
  createTaxSoftwareIntegration,
  saveApprovedIPAddresses,
  updateTaxSoftwarePasswords,
  stopTaxSoftwareParser,
  getCurrentCompany,
  saveMyCloud,
  getStripeAccountLink,
  getStripeOAuthURL,
  stripeOAuthCallback,
  checkSharepointIntegration,
  checkStripeIntegration,
  getAllWebhooks,
  addWebhook,
  deleteWebhook,
  resyncSharepoint,
};
